/**
 * Equity Core
 *
 * Instrument-agnostic equity primitives shared between the job-offer comparison
 * engine (`job-offer-engine.ts`) and the standalone equity portfolio engine
 * (`equity-portfolio-engine.ts`).
 *
 * These were originally authored inside the job-offer engine and extracted here
 * verbatim so both calculators reuse the same well-tested math. The job-offer
 * engine re-exports every symbol below, so its public surface is unchanged.
 */

// ═══════════════════════════════════════════════════════════════════
// TAX PRESETS
// ═══════════════════════════════════════════════════════════════════

export interface TaxRates {
  /** Ordinary income (RSU vest, NSO spread, salary marginal) */
  ord: number;
  /** Long-term capital gains */
  ltcg: number;
  /** State + city portion applied to ISO spread at exercise (regular tax path) */
  isoStateCity: number;
  /** Federal AMT rate on ISO spread if held for LTCG treatment */
  isoAmt: number;
  /** Effective rate on cash comp (below top marginal) */
  cashEff: number;
}

export type TaxPresetKey =
  | 'nyc-single-2026'
  | 'ny-state-single'
  | 'ca-single'
  | 'wa-tx-fl-single'
  | 'ma-single'
  | 'nj-single'
  | 'custom';

export type FilingStatus = 'single' | 'married_joint';

export const TAX_PRESETS: Record<Exclude<TaxPresetKey, 'custom'>, TaxRates> = {
  // Preserved verbatim from reference model:
  // ord: 37% fed + 10.9% NY + 3.876% NYC + ~2.35% Medicare add'l ≈ 54.1%
  // ltcg: 20% fed + 3.8% NIIT + 10.9% NY + 3.876% NYC ≈ 38.6%
  // isoStateCity: NY state + NYC on ISO spread at exercise
  // isoAmt: federal AMT rate on ISO spread
  'nyc-single-2026': { ord: 0.541, ltcg: 0.386, isoStateCity: 0.148, isoAmt: 0.28, cashEff: 0.38 },
  'ny-state-single': { ord: 0.502, ltcg: 0.347, isoStateCity: 0.109, isoAmt: 0.28, cashEff: 0.36 },
  'ca-single': { ord: 0.541, ltcg: 0.371, isoStateCity: 0.133, isoAmt: 0.28, cashEff: 0.38 },
  'wa-tx-fl-single': { ord: 0.408, ltcg: 0.238, isoStateCity: 0.0, isoAmt: 0.28, cashEff: 0.3 },
  'ma-single': { ord: 0.45, ltcg: 0.288, isoStateCity: 0.05, isoAmt: 0.28, cashEff: 0.33 },
  'nj-single': { ord: 0.513, ltcg: 0.353, isoStateCity: 0.1075, isoAmt: 0.28, cashEff: 0.36 }
};

export const TAX_PRESET_LABELS: Record<TaxPresetKey, string> = {
  'nyc-single-2026': 'New York City (2026)',
  'ny-state-single': 'New York State (no city)',
  'ca-single': 'California',
  'wa-tx-fl-single': 'No state income tax (WA/TX/FL)',
  'ma-single': 'Massachusetts',
  'nj-single': 'New Jersey',
  custom: 'Custom rates'
};

/**
 * Married-filing-jointly shifts thresholds; for top-bracket approximation we
 * drop ordinary and cashEff by a fixed 4 percentage points. LTCG and AMT stay.
 */
export function resolveTaxRates(preset: TaxPresetKey, filing: FilingStatus, custom?: TaxRates): TaxRates {
  const base = preset === 'custom' ? (custom ?? TAX_PRESETS['nyc-single-2026']) : TAX_PRESETS[preset];
  if (filing === 'married_joint') {
    return {
      ...base,
      ord: Math.max(0, base.ord - 0.04),
      cashEff: Math.max(0, base.cashEff - 0.04)
    };
  }
  return base;
}

/** IRC §422(d): ISO shares first exercisable in a year, by grant-date FMV × count. */
export const ISO_100K_LIMIT = 100_000;

/** QSBS §1202 federal exemption cap. */
export const QSBS_CAP = 10_000_000;

// ═══════════════════════════════════════════════════════════════════
// VEST FREQUENCY
// ═══════════════════════════════════════════════════════════════════

export type RsuVestFrequency = 'monthly' | 'quarterly' | 'annual';

// ═══════════════════════════════════════════════════════════════════
// POOL HELPERS — ported verbatim from reference
// Lots: [{ valBase, basis }]. valBase is value at grant-date valuation;
// current value = valBase × (curVal / grantDateVal).
// ═══════════════════════════════════════════════════════════════════

export interface PoolLot {
  id: string;
  valBase: number;
  basis: number;
}

export function poolTotal(pool: PoolLot[], curVal: number, grantDateVal: number): number {
  return pool.reduce((s, l) => s + l.valBase * (curVal / grantDateVal), 0);
}

export function poolSell(
  pool: PoolLot[],
  frac: number,
  curVal: number,
  grantDateVal: number,
  ltcgRate: number
): { pool: PoolLot[]; proceeds: number; ltcgTax: number; net: number } {
  let proceeds = 0;
  let ltcgTax = 0;
  const next = pool.map((l) => {
    const val = l.valBase * (curVal / grantDateVal);
    proceeds += val * frac;
    ltcgTax += Math.max(0, val - l.basis) * frac * ltcgRate;
    return { ...l, valBase: l.valBase * (1 - frac), basis: l.basis * (1 - frac) };
  });
  return { pool: next, proceeds, ltcgTax, net: proceeds - ltcgTax };
}

// ═══════════════════════════════════════════════════════════════════
// VESTING SCHEDULE
// ═══════════════════════════════════════════════════════════════════

/**
 * Build a per-year USD vest schedule from total value + cliff + frequency.
 * Cliff months delay the first vest events; backed-up units release at the
 * cliff boundary. Units after the cliff vest at the chosen frequency and are
 * bucketed into the year in which they vest.
 *
 * Returned array has length = horizon.
 */
export function buildVestSchedule(
  totalValue: number,
  vestYears: number,
  cliffMonths: number,
  frequency: RsuVestFrequency,
  horizon: number
): number[] {
  const out = new Array(horizon).fill(0) as number[];
  if (totalValue <= 0 || vestYears <= 0) return out;
  const totalMonths = vestYears * 12;
  const cliff = Math.max(0, Math.min(cliffMonths, totalMonths));
  const tickMonths = frequency === 'monthly' ? 1 : frequency === 'quarterly' ? 3 : 12;
  const perMonth = totalValue / totalMonths;
  // Whether the full vest schedule finishes within the projected window. When
  // the projection horizon is shorter than the vesting period, the loop below
  // exits early via `break` with real, still-unvested value left in `rem` —
  // that value must stay unvested, not get swept into the remainder-dump below.
  const scheduleFitsInHorizon = totalMonths <= horizon * 12;
  let vestedSoFar = 0;
  for (let m = 1; m <= totalMonths; m += 1) {
    const yrIdx = Math.floor((m - 1) / 12);
    if (yrIdx >= horizon) break;
    if (m < cliff) continue;
    if (m === cliff) {
      const backedUp = perMonth * cliff;
      out[yrIdx] += backedUp;
      vestedSoFar += backedUp;
      continue;
    }
    if ((m - cliff) % tickMonths === 0) {
      const chunk = perMonth * tickMonths;
      out[yrIdx] += chunk;
      vestedSoFar += chunk;
    }
  }
  // Handle any fractional remainder from rounding, but only when the schedule
  // actually completed inside the horizon — otherwise this used to dump every
  // still-unvested share into the last visible year as if it had vested just
  // because the chart window ended first (e.g. a $600k six-year grant seen over
  // a three-year horizon reported the full $600k vested instead of $300k).
  if (scheduleFitsInHorizon) {
    const rem = totalValue - vestedSoFar;
    if (Math.abs(rem) > 0.01) {
      const lastIdx = Math.min(horizon - 1, Math.max(0, vestYears - 1));
      out[lastIdx] += rem;
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════
// FORMATTERS
// ═══════════════════════════════════════════════════════════════════

export function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const a = Math.abs(n);
  const s =
    a >= 1e9
      ? `$${(a / 1e9).toFixed(2)}B`
      : a >= 1e6
        ? `$${(a / 1e6).toFixed(2)}M`
        : a >= 1000
          ? `$${Math.round(a / 1000)}k`
          : `$${Math.round(a)}`;
  return n < 0 ? `-${s}` : s;
}

export function fmtN(n: number): string {
  return Math.round(n).toLocaleString();
}

export function pctFmt(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}
